import { useNavigate } from "@tanstack/react-router";
import { useT } from "../../i18n";
import { FehlerBand, Marke } from "../../bausteine";
import { Handyliste, Handyzeile } from "../../bausteine/Handyzeile";
import { useJetzt } from "../../bausteine/Einsatzuhr";
import { datumKurz, uhr, useMeinTag } from "../../daten/einsatzzeit";
import type { Einsatz, OffenerEintrag } from "../../daten/einsatzzeit";
import { dauer, stunden } from "../../daten/einsatzlage";
import { useWarteschlange } from "../../daten/warteschlange";
import "../../stile/einsatzzeit.css";

/* ══════════════════════════════════════════════════════════════════
   Die Zeit am Auftrag — heute, nur zum Lesen.

   Luca, 16.09.2026: *„Ich fahr morgens hin und stempel abends auf dieser
   Baustelle wieder aus. Und in der Planung sieht man live die Zeit, die er
   bis jetzt getrackt hat."*

   ── Entflochten am 17.09.2026 ─────────────────────────────────────

   Bis hierher war das eine Karte je Einsatz mit dem Knopf zum Einstempeln,
   dem Knopf zum Bestätigen und darin, aufgeklappt, noch einmal eine Karte
   mit der Unterschriftsfläche. Zwei gelbe Hauptknöpfe konkurrierten, und
   dieselbe Karte stand auch im Übergabeblatt.

   **Gehandelt wird jetzt im Einsatz** (`masken/handy/Einsatz.tsx`): Anfahrt,
   „Auf der Baustelle", Tag abschließen, Stunden bestätigen lassen. **Hier
   steht, was ist** — je Einsatz eine Zeile, und die Zeile führt dorthin.

   * **Vergessen.** Steht ein Eintrag von gestern offen, steht er oben in
     Rot, mit dem Weg: Das Ende trägt die Disposition nach.
   * **Was im Gerät liegt**, zählt mit — wer im Funkloch begonnen hat, sieht
     die Zeit trotzdem laufen.

   **Regel 9.** Die Zeit gehört dem Auftrag. Eine Woche, ein Monat, ein Konto
   — nichts davon steht hier.

   ── Verwaist seit dem 18.09.2026 — mit Absicht ────────────────────

   Luca: *„kann weg? wir erfassen die zeiten ja über die vorgänge."* Der
   Schirm `/handy/zeiten`, der einzige Leser dieses Bausteins, ist damit weg
   (`router.tsx` leitet die Adresse auf „Heute" um). Die Liste selbst fehlt
   niemandem: „Heute" zeigt dieselben Einsätze mit derselben Uhr und führt in
   dieselbe Maske.

   **Eine Anzeige darin steht aber sonst nirgends am Telefon: `Vergessen`.**
   Sie sagt dem Fahrer, dass von gestern ein Eintrag offen ist — er hat das
   Ausstempeln vergessen. Am Schreibtisch sieht die Disposition es
   (`planung/Einsatzzeiten.tsx`, `bausteine/Stundenaufstellung.tsx`), und die
   API weist den nächsten Stempelversuch mit `zeit.vortag_offen` ab. Aber
   **vorher**, ungefragt, sagt es nur dieser Kasten.

   Darum ist die Datei nicht gelöscht, sondern steht hier und wartet: Ihr
   Platz wäre der Kopf der Tagesmappe (`masken/Handy.tsx`, das `useMeinTag()`
   ohnehin schon liest) — drei Zeilen, die niemand in dieser Sitzung
   schreiben durfte. Eine Anzeige still zu entfernen, die jemand braucht, ist
   teurer als eine Datei, die einen Kommentar trägt.
   ══════════════════════════════════════════════════════════════════ */

/**
 * **Vergessen.** Steht ein Eintrag von gestern offen, sagt es diese Zeile —
 * ungefragt und bevor jemand den nächsten Stempelversuch macht.
 *
 * Sie ist seit dem 18.09.2026 **exportiert**, weil ihr einziger Leser, der
 * Schirm `/handy/zeiten`, an diesem Tag weggefallen ist (er war eine zweite
 * Liste für dieselbe Sache). Die Warnung war damit am Telefon nirgends mehr
 * zu sehen: Die Disposition sieht den Zustand am Schreibtisch, und die API
 * weist den nächsten Stempelversuch mit `zeit.vortag_offen` ab — aber
 * *vorher* und *ungefragt* sagte es nur dieser Kasten. Er steht jetzt im
 * Kopf der Tagesmappe (`masken/Handy.tsx`).
 */
export function Vergessen({ e }: { e: OffenerEintrag }) {
  const t = useT();
  return (
    <p className="einsatz-vergessen" role="alert">
      <b>{t("einsatzzeit.handyVergessenTitel", { tag: datumKurz(e.seit), uhr: uhr(e.seit) })}</b>
      <span>{[e.kunde, e.vorgang].filter(Boolean).join(` ${t("allgemein.trennpunkt")} `)}</span>
      <span>{t("einsatzzeit.handyVergessenSatz")}</span>
    </p>
  );
}

/** Was an einer Bediener-Belegung gerade läuft — vom Server und vom Gerät. */
function lageVon(e: Einsatz, wartende: ReturnType<typeof useWarteschlange>) {
  let laeuft: { art: "anfahrt" | "arbeit"; seit: string } | null = e.unterwegs
    ? { art: "anfahrt", seit: e.unterwegs.seit }
    : e.laeuft ? { art: "arbeit", seit: e.laeuft.seit } : null;
  let bestaetigt = e.quittiertAm !== null;
  for (const w of wartende) {
    if (w.abgewiesen || w.nutzlast["belegung_id"] !== e.belegungId) continue;
    if (w.weg === "/handy/einsatz/losfahren") laeuft = { art: "anfahrt", seit: w.erfasstAm };
    else if (w.weg === "/handy/einsatz/angekommen") laeuft = { art: "arbeit", seit: w.erfasstAm };
    else if (w.weg === "/handy/einsatz/ausstempeln") laeuft = null;
    else if (w.weg === "/zeiten/quittung") bestaetigt = true;
  }
  return { laeuft, bestaetigt };
}

/**
 * @param ohneTitel Auf einem eigenen Schirm steht der Name schon im
 *   Maskenkopf (`handy/Zeiten.tsx`).
 */
export function MeinEinsatz({ ohneTitel }: { ohneTitel?: boolean }) {
  const t = useT();
  const gehZu = useNavigate();
  const { data, isError, refetch } = useMeinTag();
  const wartende = useWarteschlange();
  const lagen = (data?.heute ?? []).map((e) => ({ e, ...lageVon(e, wartende) }));
  const jetzt = useJetzt(lagen.some((l) => l.laeuft) ? 30_000 : null);

  if (isError) return <FehlerBand pfad="einsatzzeit.handyLadeFehler" nochmal={() => void refetch()} />;
  // Wer heute nicht als Bediener fährt, sieht nichts — auch keinen Ladezustand.
  if (!data || (data.heute.length === 0 && data.vortagOffen.length === 0)) return null;
  const trenn = ` ${t("allgemein.trennpunkt")} `;

  return (
    <section className="handy-block einsatz-block" aria-label={t("einsatzzeit.handyTitel")}>
      {ohneTitel ? null : <h3>{t("einsatzzeit.handyTitel")}<i>{data.heute.length}</i></h3>}
      {data.vortagOffen.map((e) => <Vergessen key={e.id} e={e} />)}
      {lagen.length ? (
        <Handyliste>
          {lagen.map(({ e, laeuft, bestaetigt }) => {
            const laufend = laeuft?.art === "arbeit" ? Math.max(0, (jetzt - new Date(laeuft.seit).getTime()) / 1000) : 0;
            const heute = e.sekundenHeute + laufend;
            return (
              <Handyzeile
                key={e.belegungId}
                streifen={laeuft ? "raus" : "still"}
                tun={() => void gehZu({ to: "/einsatz/$belegung", params: { belegung: e.belegungId } })}
                kennung={e.geraete || e.vorgang}
                sache={e.position}
                rechts={laeuft
                  ? t("einsatz.heute.laeuftUhr", { dauer: dauer(laeuft.art === "anfahrt" ? (jetzt - new Date(laeuft.seit).getTime()) / 1000 : heute) })
                  : t("einsatz.zeitenSchirm.heute", { stunden: stunden(heute) })}
                kunde={[e.kunde || t("handy.ohneKunde"), [e.ort, e.ortschaft].filter(Boolean).join(trenn)].filter(Boolean).join(trenn)}
                marken={(
                  <>
                    {laeuft ? (
                      <Marke art="gut" punkt>{t(laeuft.art === "anfahrt" ? "einsatz.heute.unterwegs" : "einsatz.heute.vorOrt")}</Marke>
                    ) : null}
                    {bestaetigt ? <Marke art="gut">{t("einsatzzeit.bestaetigtMarke")}</Marke>
                      : e.eintraegeHeute > 0 && !laeuft ? <Marke art="acht" punkt>{t("einsatz.zeitenSchirm.nichtBestaetigt")}</Marke>
                        : null}
                  </>
                )}
              />
            );
          })}
        </Handyliste>
      ) : null}
    </section>
  );
}
